import { useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import { inputClass, RequiredMark } from "../../components/forms/Field";
import {
  BTN,
  Card,
  EmptyState,
  ErrorState,
  Notice,
  Refreshing,
  ROW_BTN,
  STALE,
  TABLE_HEAD,
  TableSkeleton,
} from "../../components/timetable/PanelKit";
import ModalShell from "../Templates/ModalShell";
import ConfirmModal from "../Templates/ConfirmModal";
import { formatApiError } from "../../utils/helpers/apiErrors";
import {
  formatMonthShort,
  formatShortDate,
  parseLocalISODate,
} from "../../utils/helpers/dateDisplay";
import {
  createAcademicTerm,
  deleteAcademicTerm,
  getAcademicTerms,
  updateAcademicTerm,
} from "../../services/schedulingService";

// ---------------------------------------------------------------------------
// The school's term calendar.
//
// `AcademicTerm` had no owner until now: four rows seeded by a migration were
// the whole network's calendar, and a school whose year is shaped differently
// could say so only through Django admin. It has an optional organization now,
// and resolution is ALL-OR-NOTHING — a school with any terms of its own uses
// ONLY those, a school with none uses the defaults. Never a merge: a school's
// own first term is expected to sit on top of the default it replaces, so the
// union of the two sets is a calendar with overlapping windows, where the term
// a lesson falls into depends on which row the scan reached first.
//
// That rule is the whole reason this card exists and the whole reason it is
// shaped the way it is:
//
//   * WHICH OF THE TWO STATES the school is in is stated in words, never left
//     to be inferred from a disabled button.
//   * The only way OUT of the defaults is "copy them and start from that" —
//     there is deliberately no "add one term" button while a school is still on
//     the defaults. That single write would switch the school off all four of
//     them, leaving a calendar with one term in it: eleven months of the year
//     would then belong to no term at all, and the coverage page, the lesson
//     curriculum and the generator would all quietly read as empty.
//
// The card is a section of the School week tab, not a page: the workspace shell
// owns the width, the back link and the `organization.timetable.manage` guard
// this endpoint's writes are gated on, so everyone who can see this can write.
// ---------------------------------------------------------------------------

// The list endpoint is unpaginated, but every other list in this module tolerates
// both shapes and a bare array is easy to turn into an envelope later.
const rowsOf = (data) => (Array.isArray(data) ? data : data?.results || []);

// Days per month with February at 28, NOT 29.
//
// A term boundary has no year — the same MM-DD is reused every school year — so
// 02-29 is a boundary that exists in one year out of four. In the other three
// `AcademicTerm.for_date` skips the row as unresolvable, so the term would sit
// in this list looking perfectly normal while matching no lesson at all. The
// server refuses it too, but only in the years it cannot resolve, which is the
// same rule arriving three years late.
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Any non-leap year: this date is only ever fed to the month formatter.
const MONTH_LABEL_YEAR = 2001;

const pad2 = (n) => String(n).padStart(2, "0");

// "MM-DD" -> { month: 1..12, day: 1..31 }, with "" for either half not yet set.
const splitMonthDay = (value) => {
  const match = /^(\d{2})-(\d{2})$/.exec(String(value || ""));
  return match
    ? { month: Number(match[1]), day: Number(match[2]) }
    : { month: "", day: "" };
};

// Sorted into ACADEMIC-YEAR order, which is not the order the API returns.
//
// The model orders by the raw `date_from` string, so the school year reads
// 01-01, 04-01, 08-10, 11-01 — third term, fourth term, first term, second.
// The resolved `start_date` the serializer publishes already carries the pivot
// that puts 01-01 in the NEXT calendar year, so sorting on it is the school's
// own reading order without re-deriving the pivot rule in the browser.
const inYearOrder = (terms) =>
  [...terms].sort((a, b) => String(a.start_date || "").localeCompare(String(b.start_date || "")));

// Field errors, kept next to the field that caused them.
//
// The overlap message is the one this form exists to surface: the server hangs
// it on `date_from`, and a toast four inches from the two selects that produced
// it would be the one place a manager is not looking. `formatApiError` is still
// the fallback for everything without a field — a 403, a 500, a bare string.
const FORM_FIELDS = ["name", "date_from", "date_till"];

const firstMessage = (value) => {
  if (Array.isArray(value)) return firstMessage(value[0]);
  return typeof value === "string" ? value : "";
};

const fieldErrors = (err) => {
  const data = err?.response?.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};
  const next = {};
  FORM_FIELDS.forEach((field) => {
    const message = firstMessage(data[field]);
    if (message) next[field] = message;
  });
  return next;
};

export default function AcademicTermsCard() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);

  // Whether this account is staffed at a school AT ALL. It separates the two
  // read-only readings of the default calendar: a school that has not opted out
  // yet (and can), versus an account with no school to opt out WITH. Every write
  // here is filed against the caller's own staff link, so without one the only
  // thing this card can ever show is the network's shared set — and offering
  // "set up our own calendar" there would be a button that can only 403.
  const hasSchool = (user?.organizations || []).some((link) => link.is_in_progress);

  const [terms, setTerms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [editing, setEditing] = useState(null); // term | "new" | null
  const [deleting, setDeleting] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStartingOwn, setIsStartingOwn] = useState(false);
  const [confirmStartOwn, setConfirmStartOwn] = useState(false);

  // No `?organization=`: the server resolves the caller's own school from their
  // staff link, and that is the only reading guaranteed to match what every
  // WRITE here is filed against. `/auth/me/` orders `organizations` by
  // `-is_in_progress, -start_date` while the server takes the earliest link by
  // `created_at`, so a manager staffed at two schools would otherwise be shown
  // one school's calendar and edit the other's.
  const load = useCallback((signal) => {
    setIsLoading(true);
    getAcademicTerms({ signal })
      .then((data) => {
        setTerms(rowsOf(data));
        setLoadError(false);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        // No toast: the failure renders as the panel below (first load) or the
        // strip above the rows (refetch), and a toast would only repeat it.
        setLoadError(true);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const ordered = useMemo(() => inYearOrder(terms), [terms]);

  // One coherent calendar arrives per request, so the state is a property of the
  // whole list rather than of a row. `terms.length > 0` guards the vacuous
  // `every` on an empty list, which would otherwise read as "own calendar" for a
  // network with no terms seeded at all.
  const isOwnCalendar =
    terms.length > 0 && terms.every((term) => !term.is_network_default);
  const canEdit = isOwnCalendar && hasSchool;

  // The concrete symptom of a half-built calendar, and the only one a manager
  // would otherwise meet on a different page: today in no term means the
  // coverage page has no current term to offer and a lesson filed today belongs
  // to no period. Only asked of a school's own calendar — the network defaults
  // are not this school's to fix.
  const coversToday = ordered.some((term) => term.is_current);

  const startOwnCalendar = async () => {
    setIsStartingOwn(true);
    let created = 0;
    try {
      // Sequential, not `Promise.all`. Each write is validated against the terms
      // already filed for this school, so four in flight at once would each see
      // a different subset of their siblings and the overlap rule would pass or
      // fail by arrival order. Four requests is the entire calendar — this is
      // not a loop that grows with the data.
      for (const term of ordered) {
        // `code` is deliberately not copied: nothing resolves a term by it, and
        // two calendars whose rows both claim `term_1` is a false identity that
        // invites someone to add the unique constraint the model refuses.
        await createAcademicTerm({
          body: {
            name: term.name,
            date_from: term.date_from,
            date_till: term.date_till,
          },
        });
        created += 1;
      }
      toast.success(t("timetable.terms.ownCalendarStarted"));
    } catch (err) {
      // A partial copy has ALREADY taken the school off the defaults, so the
      // honest message is not "nothing happened" — it is "your calendar is now
      // your own and it is missing terms". Nothing here can roll the first three
      // writes back; the list below shows what landed and the warning above it
      // says what that costs.
      toast.error(
        created === 0
          ? formatApiError(err, t("timetable.terms.ownCalendarFailed"))
          : t("timetable.terms.ownCalendarPartial"),
      );
    } finally {
      setIsStartingOwn(false);
      setConfirmStartOwn(false);
      load();
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await deleteAcademicTerm({ uuid: deleting.uuid });
      toast.success(t("timetable.terms.deleted"));
      setDeleting(null);
      load();
    } catch (err) {
      // A term held by a lesson, a generation run or a filed observation comes
      // back as one sentence naming all three — PROTECT, not a 500.
      toast.error(formatApiError(err, t("timetable.terms.deleteFailed")));
    } finally {
      setIsDeleting(false);
    }
  };

  const isRefreshing = isLoading && terms.length > 0;

  return (
    <>
      <section className="space-y-4">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {/* h3, like the two cards above it — the panel's own <h2> is the
                tab title, and this is a section of that tab. */}
            <h3 className="text-sm font-bold text-slate-900">
              {t("timetable.terms.title")}
            </h3>
            <p className="mt-1 max-w-2xl text-xs font-medium leading-relaxed text-slate-500">
              {t("timetable.terms.intro")}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {isRefreshing && <Refreshing />}
            {terms.length > 0 && hasSchool && (
              canEdit ? (
                <button
                  type="button"
                  onClick={() => setEditing("new")}
                  className={BTN.primary}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {t("timetable.terms.add")}
                </button>
              ) : (
                // The ONE action that makes sense on a read-only calendar. Not
                // "add a term": that write alone would drop the other three.
                <button
                  type="button"
                  onClick={() => setConfirmStartOwn(true)}
                  className={BTN.primary}
                >
                  {t("timetable.terms.startOwn")}
                </button>
              )
            )}
          </div>
        </header>

        {isLoading && terms.length === 0 ? (
          <TableSkeleton
            rows={4}
            columns={["w-24", "w-56", "w-16 hidden md:block", "w-28"]}
          />
        ) : loadError && terms.length === 0 ? (
          <ErrorState
            title={t("timetable.terms.loadFailed")}
            onRetry={() => load()}
          />
        ) : terms.length === 0 ? (
          // Neither the school's own terms nor a network default — a network
          // whose calendar was never seeded. Nothing on this screen can fix it,
          // so it says what is missing rather than offering a button.
          <EmptyState
            iconPath="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            title={t("timetable.terms.empty")}
            hint={t("timetable.terms.emptyHint")}
          />
        ) : (
          <>
            <Card padded={false}>
              {loadError && (
                <div
                  role="alert"
                  className="border-b border-rose-100 bg-rose-50 px-6 py-2.5 text-[11px] font-black text-rose-700"
                >
                  {t("timetable.terms.refreshFailed")}
                </div>
              )}

              {/* WHICH CALENDAR THIS IS, in words. The distinction is the whole
                  feature: one of these two lists is editable and the other is
                  every school's, and a greyed-out button says neither. */}
              <div
                className={`border-b px-6 py-4 ${
                  isOwnCalendar
                    ? "border-indigo-100 bg-indigo-50/60"
                    : "border-slate-100 bg-slate-50"
                }`}
              >
                <span
                  className={`inline-block rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                    isOwnCalendar
                      ? "bg-indigo-100 text-indigo-700"
                      : "bg-slate-200 text-slate-700"
                  }`}
                >
                  {isOwnCalendar
                    ? t("timetable.terms.ownChip")
                    : t("timetable.terms.defaultChip")}
                </span>
                <p className="mt-2 max-w-3xl text-xs font-medium leading-relaxed text-slate-600">
                  {isOwnCalendar
                    ? t("timetable.terms.ownBody")
                    : hasSchool
                      ? t("timetable.terms.defaultBody")
                      : t("timetable.terms.noSchoolBody")}
                </p>
              </div>

              <div className={`overflow-x-auto ${isLoading ? STALE : ""}`}>
                {/* `px-4` below `sm`, `px-6` from there: three columns of
                    48px gutters is 144px of a 342px phone, which is what turned
                    a four-row table into a horizontal scroll. Identical to every
                    other table in the workspace at any width people work at. */}
                <table className="w-full text-sm" aria-busy={isLoading}>
                  <thead>
                    <tr className={TABLE_HEAD}>
                      <th className="px-4 py-3 sm:px-6">{t("timetable.terms.colName")}</th>
                      <th className="px-4 py-3 sm:px-6">{t("timetable.terms.colDates")}</th>
                      <th className="hidden px-4 py-3 sm:px-6 md:table-cell">
                        {t("timetable.terms.colBoundaries")}
                      </th>
                      {/* No empty action column on a calendar nobody can edit:
                          four blank cells under a blank heading read as a table
                          that failed to render, and the banner above has already
                          said why the rows are read-only. */}
                      {canEdit && <th className="px-4 py-3 sm:px-6" />}
                    </tr>
                  </thead>
                  <tbody>
                    {ordered.map((term) => (
                      <TermRow
                        key={term.uuid}
                        term={term}
                        canEdit={canEdit}
                        onEdit={() => setEditing(term)}
                        onDelete={() => setDeleting(term)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {isOwnCalendar && !coversToday && (
              <Notice tone="amber" title={t("timetable.terms.gapTitle")}>
                {t("timetable.terms.gapBody")}
              </Notice>
            )}
          </>
        )}

        {/* Said once, quietly, and only here: an admin changing these dates is
            changing what two other screens measure. */}
        <p className="max-w-2xl text-xs leading-relaxed text-slate-500">
          <span className="font-bold text-slate-600">
            {t("timetable.terms.usedWhereTitle")}
          </span>{" "}
          {t("timetable.terms.usedWhere")}
        </p>
      </section>

      {editing && (
        <TermFormModal
          term={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {confirmStartOwn && (
        <ConfirmModal
          title={t("timetable.terms.startOwnTitle")}
          body={t("timetable.terms.startOwnBody")}
          confirmLabel={
            isStartingOwn
              ? t("timetable.terms.startingOwn")
              : t("timetable.terms.startOwnConfirm")
          }
          busy={isStartingOwn}
          onConfirm={startOwnCalendar}
          onClose={() => setConfirmStartOwn(false)}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t("timetable.terms.deleteTitle")}
          body={t("timetable.terms.deleteBody", { term: deleting.name || "" })}
          confirmLabel={
            isDeleting ? t("timetable.terms.deleting") : t("timetable.terms.delete")
          }
          danger
          busy={isDeleting}
          onConfirm={confirmDelete}
          onClose={() => setDeleting(null)}
        >
          {/* Removing the last own term is not a deletion, it is a return to the
              shared calendar — and that is a much bigger thing than removing one
              of four, so it is said at the moment it applies rather than hidden
              behind a separate "revert" button nobody would find. */}
          {ordered.length === 1 && (
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {t("timetable.terms.deleteLastNote")}
            </p>
          )}
        </ConfirmModal>
      )}
    </>
  );
}

// -- table row ---------------------------------------------------------------

function TermRow({ term, canEdit, onEdit, onDelete }) {
  const { t } = useTranslation();

  // The RESOLVED dates, straight from the server. `date_from`/`date_till` are
  // year-less MM-DD that only the backend's PIVOT_MONTH rule turns into days,
  // and re-deriving that rule in the browser would be a second copy of it, free
  // to drift from the one every term-scoped count was measured with — the same
  // reason Coverage's PeriodSelect reads its ranges off the response.
  // `parseLocalISODate`, never `new Date("2026-08-10")`: date-only strings parse
  // as UTC and land on the previous day west of Greenwich.
  const start = formatShortDate(parseLocalISODate(term.start_date));
  const end = formatShortDate(parseLocalISODate(term.end_date));

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-900">{term.name || "—"}</span>
          {term.is_current && (
            <span className="inline-block rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600">
              {t("timetable.terms.currentChip")}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-600 sm:px-6">
        {start} — {end}
      </td>
      {/* The stored value, kept visible: a manager who edits a boundary is
          editing a day and a month, and the column beside it shows which YEAR
          those landed in this time round — which is exactly the part that
          surprises. Dropped below `md`, where the resolved dates matter more. */}
      <td className="hidden px-4 py-3 sm:px-6 md:table-cell">
        <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-500">
          {term.date_from} → {term.date_till}
        </span>
      </td>
      {canEdit && (
        <td className="px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-center justify-end gap-1">
            <button type="button" onClick={onEdit} className={ROW_BTN.edit}>
              {t("timetable.terms.edit")}
            </button>
            <button type="button" onClick={onDelete} className={ROW_BTN.delete}>
              {t("timetable.terms.delete")}
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}

// -- the MM-DD control -------------------------------------------------------

// A day and a month, as two selects — NOT `<input type="date">`.
//
// A term boundary genuinely has no year: the same "08-10" is term 1's start
// every school year, and which calendar year it lands in is decided per year by
// the backend's pivot rule. A date picker would demand a year anyway, so a
// manager would have to invent one and then be left wondering which year they
// had committed their school to. Two selects can only express what the field
// can hold, and the resolved dates in the table above say what it came out as.
//
// Day BEFORE month, matching how Kazakh and Russian say a date — and how this
// codebase's own Kazakh date formatters write one.
function MonthDayInput({ label, value, onChange, disabled, error, dayAria, monthAria }) {
  const generated = useId();
  const labelId = `${generated}-label`;
  const errorId = `${generated}-error`;

  // The two halves are held HERE rather than derived from `value` on each
  // render. A boundary only becomes an MM-DD once BOTH have been chosen, so
  // between the first click and the second the parent has nothing to hand back
  // — deriving the selects from it would blank the day the instant it was
  // picked, and the control could never be filled in at all. `value` seeds the
  // pair; the parent hears the result through `onChange`, which reports "" for
  // as long as this is half a date and cannot be saved.
  const [pair, setPair] = useState(() => splitMonthDay(value));
  const { month, day } = pair;
  const maxDay = month ? DAYS_IN_MONTH[month - 1] : 31;

  const commit = (nextMonth, nextDay) => {
    setPair({ month: nextMonth, day: nextDay });
    onChange(
      nextMonth && nextDay ? `${pad2(nextMonth)}-${pad2(nextDay)}` : "",
    );
  };

  const pickMonth = (raw) => {
    const nextMonth = raw ? Number(raw) : "";
    // Shortening the month under a day it no longer has would otherwise send
    // "02-31", which the server rejects as "not a real calendar date" — a true
    // sentence about a value the operator never chose.
    const nextDay =
      nextMonth && day ? Math.min(day, DAYS_IN_MONTH[nextMonth - 1]) : day;
    commit(nextMonth, nextDay);
  };

  // `Field` is not used here on purpose: it associates a label with a SINGLE
  // cloned child, and this control is two. Rather than point `htmlFor` at a
  // wrapper div — an association a screen reader ignores — the pair is a labelled
  // group and each select carries its own name. The classes are Field's, so the
  // control still reads as one of this form's fields.
  return (
    <div role="group" aria-labelledby={labelId}>
      <p id={labelId} className="mb-1.5 block text-xs font-semibold text-slate-600">
        {label}
      </p>
      <div className="flex gap-2">
        <select
          value={day}
          disabled={disabled}
          aria-label={dayAria}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(e) => commit(month, e.target.value ? Number(e.target.value) : "")}
          className={`${inputClass(error)} w-24 shrink-0`}
        >
          <option value="">—</option>
          {Array.from({ length: maxDay }).map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}
            </option>
          ))}
        </select>
        <select
          value={month}
          disabled={disabled}
          aria-label={monthAria}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(e) => pickMonth(e.target.value)}
          className={inputClass(error)}
        >
          <option value="">—</option>
          {DAYS_IN_MONTH.map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {/* Month names through the app's own formatter, which already
                  carries the Kazakh table for runtimes with no `kk` CLDR data —
                  where Intl silently answers "M08" instead. */}
              {formatMonthShort(new Date(MONTH_LABEL_YEAR, i, 1))}
            </option>
          ))}
        </select>
      </div>
      {error && (
        <p id={errorId} className="mt-1 text-xs text-rose-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// -- create / edit modal -----------------------------------------------------

function TermFormModal({ term, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = Boolean(term);

  const [name, setName] = useState(term?.name || "");
  const [dateFrom, setDateFrom] = useState(term?.date_from || "");
  const [dateTill, setDateTill] = useState(term?.date_till || "");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const generated = useId();
  const nameId = `${generated}-name`;
  const nameHintId = `${generated}-name-hint`;
  const nameErrorId = `${generated}-name-error`;

  // Deliberately NOT a client-side version of the server's range check. Whether
  // one boundary comes before the other is only answerable after the pivot has
  // resolved both — 11-01..01-31 is inverted as text and perfectly ordered as
  // dates — so the only checks here are the ones that need no calendar at all.
  // The overlap and ordering rules stay the server's, and land on the field.
  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = t("timetable.termForm.errName");
    if (!dateFrom) next.date_from = t("timetable.termForm.errStart");
    if (!dateTill) next.date_till = t("timetable.termForm.errEnd");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      // `organization` is never sent. Absent means the caller's own school,
      // which is the only school this card can show; an explicit value is how a
      // multi-school manager targets another of theirs, and an explicit null
      // asks for a network default only a superuser may file.
      const body = {
        name: name.trim(),
        date_from: dateFrom,
        date_till: dateTill,
      };
      if (isEdit) {
        await updateAcademicTerm({ uuid: term.uuid, body });
        toast.success(t("timetable.terms.updated"));
      } else {
        await createAcademicTerm({ body });
        toast.success(t("timetable.terms.created"));
      }
      onSaved();
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      // Only what has nowhere to sit becomes a toast. A message rendered under
      // the field AND thrown at the corner of the screen is the same sentence
      // twice, and the copy that moves is the one people read.
      if (Object.keys(fields).length === 0) {
        toast.error(formatApiError(err, t("timetable.terms.saveFailed")));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} busy={isSaving} maxWidth="max-w-lg">
      <h3 className="pr-8 text-lg font-bold text-slate-900">
        {isEdit
          ? t("timetable.termForm.editTitle")
          : t("timetable.termForm.createTitle")}
      </h3>

      <form onSubmit={submit} noValidate className="mt-5 space-y-4">
        <div>
          <label
            htmlFor={nameId}
            className="mb-1.5 block text-xs font-semibold text-slate-600"
          >
            {t("timetable.termForm.name")}
            <RequiredMark />
          </label>
          <input
            id={nameId}
            type="text"
            value={name}
            disabled={isSaving}
            aria-required="true"
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={errors.name ? nameErrorId : nameHintId}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("timetable.termForm.namePlaceholder")}
            className={inputClass(errors.name)}
          />
          <p id={nameHintId} className="mt-1 text-[11px] font-medium text-slate-500">
            {t("timetable.termForm.nameHint")}
          </p>
          {errors.name && (
            <p id={nameErrorId} className="mt-1 text-xs text-rose-600" role="alert">
              {errors.name}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <MonthDayInput
            label={t("timetable.termForm.start")}
            value={dateFrom}
            onChange={setDateFrom}
            disabled={isSaving}
            error={errors.date_from}
            dayAria={t("timetable.termForm.startDayAria")}
            monthAria={t("timetable.termForm.startMonthAria")}
          />
          <MonthDayInput
            label={t("timetable.termForm.end")}
            value={dateTill}
            onChange={setDateTill}
            disabled={isSaving}
            error={errors.date_till}
            dayAria={t("timetable.termForm.endDayAria")}
            monthAria={t("timetable.termForm.endMonthAria")}
          />
        </div>

        <p className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-xs leading-relaxed text-slate-500">
          {t("timetable.termForm.yearlessHint")}
        </p>

        <div className="mt-3 flex justify-end gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className={BTN.quiet}
          >
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={isSaving} className={BTN.primary}>
            {isSaving
              ? t("common.saving")
              : isEdit
                ? t("timetable.termForm.save")
                : t("timetable.termForm.create")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
